import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {Twinkle2Line} from '@dropbox/dig-icons/assets';
import {EmployeeRoleLevel, WorkstreamUpdate} from 'client';
import {TimeAgo} from 'components/shared/TimeAgo';
import {useEmployee} from 'hooks/useEmployee';
import {t} from 'i18next';
import {ReactNode} from 'react';

import {Avatar, AvatarIcon} from './Avatar';
import {StatusChip} from './StatusChip';

type PulseUpdate = {comment: string; created_at: string; status?: string};

export const UpdatePostLoader = ({
  ldap,
  ...props
}: {
  ldap: string;
  featured?: boolean;
  update: PulseUpdate | WorkstreamUpdate | {created_at: string};
  children: ReactNode;
  isAutomaticObjectiveStatusChange?: boolean;
}) => {
  const {employee, isLoading} = useEmployee({ldap});

  if (isLoading || !employee) {
    return null;
  }

  return <UpdatePost employee={employee} {...props} />;
};

export const UpdatePost = ({
  size = 'medium',
  employee,
  featured,
  update,
  editedOnClick,
  children,
  isAutomaticObjectiveStatusChange = false,
  includeArchived = false,
}: {
  size?: 'small' | 'medium';
  employee: EmployeeRoleLevel;
  featured?: boolean;
  update: PulseUpdate | WorkstreamUpdate | {created_at: string};
  children: ReactNode;
  editedOnClick?: () => void;
  isAutomaticObjectiveStatusChange?: boolean;
  includeArchived?: boolean;
}) => {
  // const {employee, isLoading} = useEmployee({ldap});

  // if (isLoading || !employee) {
  //   return null;
  // }

  return (
    <UpdatePost.Control
      icon={
        isAutomaticObjectiveStatusChange ? (
          <AvatarIcon
            // eslint-disable-next-line deprecation/deprecation -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
            src={Twinkle2Line}
            size={size}
            // color="Text Base"
            // background="var(--dig-color__primary__surface--state-1)"
          />
        ) : (
          <Avatar size={size} user={employee} isInactive={false} />
        )
      }
      featured={featured}
    >
      {isAutomaticObjectiveStatusChange ? (
        <Text variant="paragraph" className={atoms({display: 'block'})}>
          {t('automatic_objective_status_change')}
        </Text>
      ) : (
        <UpdatePost.User employee={employee} />
      )}

      <UpdatePost.Status
        update={update}
        editedOnClick={editedOnClick}
        includeArchived={includeArchived}
      >
        {children}
      </UpdatePost.Status>
    </UpdatePost.Control>
  );
};

const Control = ({
  icon,
  children,
  featured,
}: {
  icon: React.ReactNode;
  featured?: boolean;
  children: React.ReactNode;
}) => (
  <Stack align="start" style={{boxSizing: 'border-box', width: '100%', position: 'relative'}}>
    <Split
      gap="8"
      className={atoms({
        padding: '16',
        borderColor: 'Border Subtle',
        borderStyle: featured ? 'Solid' : undefined,
        backgroundColor: featured ? 'Background Base' : undefined,
        borderRadius: 'Medium',
      })}
    >
      <Split.Item>
        <List.Accessory>
          <Box marginTop="2">{icon}</Box>
        </List.Accessory>
      </Split.Item>
      <Split.Item style={{overflow: 'hidden'}} width="fill">
        <List.Content>{children}</List.Content>
      </Split.Item>
    </Split>
  </Stack>
);
UpdatePost.Control = Control;

const User = ({employee}: {employee: EmployeeRoleLevel}) => (
  <div>
    <div>
      <Text style={{marginRight: '4px'}} isBold>
        {employee.name ?? ''}
      </Text>
      <Text size="small" color="faint">
        {employee.role ?? ''}
      </Text>
    </div>
  </div>
);
UpdatePost.User = User;

const Content = ({children}: {children: React.ReactNode}) => (
  <Text tagName="p" color="standard">
    {children}
  </Text>
);
UpdatePost.Content = Content;

const Status = ({
  update,
  editedOnClick,
  children,
  includeArchived,
}: {
  update: PulseUpdate | {created_at: string};
  editedOnClick?: () => void;
  children: React.ReactNode;
  includeArchived?: boolean;
}) => (
  <>
    <Box display="flex" alignItems="center" style={{gap: 8}}>
      <TimeAgo timestamp={update.created_at!} />
      {editedOnClick && (
        <>
          <Text color="subtle" size="small">
            •
          </Text>
          <Button variant="transparent" size="small" onClick={editedOnClick}>
            <Text color="subtle" size="small">
              {t('edited')}
            </Text>
          </Button>
        </>
      )}
    </Box>
    {'comment' in update ? (
      <>
        {update.comment !== 'automatic_objective_status_change' && (
          <UpdatePost.Content>{children}</UpdatePost.Content>
        )}
        {update.status && (
          <Box marginTop="8" display="flex" alignItems="center">
            <StatusChip status={update.status} includeArchived={includeArchived} />
          </Box>
        )}
      </>
    ) : (
      <UpdatePost.Content>{children}</UpdatePost.Content>
    )}
  </>
);
UpdatePost.Status = Status;
